import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from "@angular/core";
import { Subject, takeUntil } from "rxjs";
import { SharedModule } from "shared-ui/shared.module";
import { CurrentTenantService } from "app/core/services/api/platform-management/current-tenant.service";
import { TenantSelectorComponent } from "app/shared/tenant-selector/tenant-selector.component";

/**
 * SL-52: inline tenant switcher for list pages (account groups, accounts, balances, wallets,
 * service offerings, websites). Changes the same workspace selection as the "Không gian doanh
 * nghiệp" dashboard through CurrentTenantService.selectTenant(), so every page that already
 * reacts to accountTenantId$()/tenantId$() refreshes without page-local filter logic.
 *
 * Renders nothing for regular tenant accounts — only the platform owner may switch tenants.
 * The dropdown itself is the shared TenantSelectorComponent (searchable dropdown-grid, floating
 * label) so the control stays a single row at the standard toolbar height.
 */
@Component({
    selector: "tenant-workspace-select",
    standalone: true,
    imports: [SharedModule, TenantSelectorComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './tenant-workspace-select.component.html',
})
export class TenantWorkspaceSelectComponent implements OnInit, OnDestroy {
    // -----------------------------------------------------------------------------------------------------
    // @ Fields
    // -----------------------------------------------------------------------------------------------------
    public canSelectTenant = false;
    public tenantId: number | null = null;
    @Input() public compact = false;
    @Input() public showAll = false;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    // -----------------------------------------------------------------------------------------------------
    // @ Constructor
    // -----------------------------------------------------------------------------------------------------
    constructor(
        private _currentTenant: CurrentTenantService,
        private _changeDetectorRef: ChangeDetectorRef,
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------
    ngOnInit(): void {
        this._currentTenant
            .canSelectTenant$()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((canSelect) => {
                this.canSelectTenant = canSelect;
                this._changeDetectorRef.detectChanges();
            });

        this._currentTenant
            .tenantId$()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((tenantId) => {
                this.tenantId = tenantId > 0 ? tenantId : null;
                this._changeDetectorRef.detectChanges();
            });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods — event handlers
    // -----------------------------------------------------------------------------------------------------
    public onTenantChanged(tenantId: number | null): void {
        const normalized = Number(tenantId);
        const nextTenantId = Number.isFinite(normalized) && normalized > 0 ? normalized : null;
        if (!this.canSelectTenant || nextTenantId === this.tenantId || (nextTenantId == null && !this.showAll)) {
            return;
        }
        this._currentTenant.selectTenant(nextTenantId);
    }
}
